/**
 * Retreat Welcome Page
 * 
 * Post-purchase congratulations page for Costa Rica retreat attendees.
 * Features countdown timer, arrival FAQs, next steps, and community access.
 * 
 * Performance optimizations:
 * - Lazy loaded confetti library
 * - Skeleton loaders for better perceived performance
 * - Optimized image loading
 */

import { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  Plane, Calendar, MapPin, Users, Clock, CheckCircle2, 
  Palmtree, Sun, Crown, Hotel, Utensils, ArrowRight,
  MessageCircle, ExternalLink, Sparkles
} from 'lucide-react';

// Lazy load confetti for better initial load performance
const loadConfetti = () => import('canvas-confetti');

// Retreat experience images
const RETREAT_IMAGES = {
  yoga: 'https://static.prod-images.emergentagent.com/jobs/639f6877-dbec-4d58-9d1f-ddaac3d1cdbf/images/727b2639e9612ce244999af857b581f7cda1ff7d1cf5509b87e28be648710b35.png',
  meditation: 'https://static.prod-images.emergentagent.com/jobs/639f6877-dbec-4d58-9d1f-ddaac3d1cdbf/images/bb91d62713fe77aa7ccf637929580d1d63245e3f21172aafe7b2a8b3abad9668.png',
  sisterhood: 'https://static.prod-images.emergentagent.com/jobs/639f6877-dbec-4d58-9d1f-ddaac3d1cdbf/images/84ec4fc6424047d49892f0fdd9410639b5f6506a7ee482d32f5724ae69dea7f7.png'
};

const API_URL = window.location.origin;

// Retreat date: June 26, 2026 9:00 AM
const RETREAT_DATE = new Date(2026, 5, 26, 9, 0, 0);

// FAQs for standard attendees
const STANDARD_FAQS = [
  {
    question: "Where do I fly into Costa Rica?",
    answer: "San Jose (Juan Santamaria) International Airport (SJO). Fly in by 7:00 AM on June 26, 2026 as we board a private jet around 9:00 AM. If arriving the day before, we can provide hotel options near SJO."
  },
  {
    question: "Do we take another airplane to Imiloa?",
    answer: "Yes! The private charter leaves at 9:00 AM from SJO domestic airport (a 5-minute walk from international arrivals). Please be at the domestic terminal by 8:15 AM on arrival day. The flight to Quepos is just 25 minutes - included in your retreat. We recommend arriving 1 day early to avoid missing group transport. Ground transportation is also available at ~$300 each way (3.5 hours) if needed."
  },
  {
    question: "Is there a luggage weight limit on the private charter?",
    answer: "Yes — the private charter to Imiloa enforces a strict 50 pound TOTAL weight limit per passenger. This includes both your carry-on and your checked bag combined. Please weigh your luggage at home before you fly. Excess weight may not be accepted on the charter and could be left behind in San José."
  },
  {
    question: "What should I pack?",
    answer: "Sunglasses, beach hat, organic sunscreen & mosquito repellent, comfortable closed-toe shoes/boots (jungle trails can be muddy), sandals, swimwear, yoga attire, flashlight, reusable water bottle, and electrolytes."
  },
  {
    question: "Is there WiFi and what about power outlets?",
    answer: "Yes, WiFi is available on campus. WhatsApp is recommended for communication. Standard North American power outlets are used - bring an adapter if needed."
  },
  {
    question: "Is there a lot of walking at Imiloa?",
    answer: "While not strenuous for most guests, you should be mobile and comfortable walking moderate distances, including stairs and hilly areas. Contact us if you have mobility concerns."
  },
  {
    question: "What is the cancellation policy?",
    answer: "All payments are non-refundable and non-transferable. Trip cancellation insurance is required to protect your investment."
  }
];

// FAQs for VIP attendees
const VIP_FAQS = [
  {
    question: "When do VIP guests arrive?",
    answer: "VIP guests arrive the day before the retreat - June 25, 2026 by 2:00 PM. You'll need to book your own hotel near SJO airport for one night (hotel details TBD - we'll update you soon)."
  },
  {
    question: "What's included in the VIP dinner?",
    answer: "Your VIP package includes an intimate welcome dinner at 6:30 PM on June 25th. This is a chance to connect with Crystal, Dr. Nicole, and fellow VIP guests before the retreat begins."
  },
  {
    question: "Where do I fly into Costa Rica?",
    answer: "San Jose (Juan Santamaria) International Airport (SJO). As a VIP, arrive by 2:00 PM on June 25, 2026. Standard guests arrive June 26th."
  },
  {
    question: "Do I join the group flight to Imiloa?",
    answer: "Yes! After your VIP night, you'll join the rest of the group on June 26th for the private charter flight. The charter departs at 9:00 AM from SJO domestic terminal - please arrive by 8:15 AM. The domestic airport is just a 5-minute walk from international arrivals."
  },
  {
    question: "Is there a luggage weight limit on the private charter?",
    answer: "Yes — the private charter to Imiloa enforces a strict 50 pound TOTAL weight limit per passenger. This includes both your carry-on and your checked bag combined. Please weigh your luggage at home before you fly. Excess weight may not be accepted on the charter and could be left behind in San José."
  },
  {
    question: "What should I pack?",
    answer: "Sunglasses, beach hat, organic sunscreen & mosquito repellent, comfortable closed-toe shoes/boots (jungle trails can be muddy), sandals, swimwear, yoga attire, flashlight, reusable water bottle, and electrolytes. For the VIP dinner, smart casual attire is perfect."
  },
  {
    question: "What other VIP perks do I receive?",
    answer: "Priority access to all experiences, exclusive VIP-only gatherings, premium amenity package, and personal concierge service throughout your stay."
  }
];

const RetreatWelcomePage = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [celebrated, setCelebrated] = useState(false);

  const bookingId = searchParams.get('booking');

  // Memoized fetch function
  const fetchWelcomeData = useCallback(async () => {
    try {
      const params = bookingId ? `?booking_id=${bookingId}` : '';
      const response = await axios.get(`${API_URL}/api/retreat/welcome-data${params}`, {
        headers: user ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}
      });
      setBooking(response.data.booking);
    } catch (error) {
      console.error('Error fetching welcome data:', error);
    } finally {
      setLoading(false);
    }
  }, [bookingId, user]);

  useEffect(() => {
    fetchWelcomeData();
  }, [fetchWelcomeData]);

  useEffect(() => {
    // Calculate countdown
    const updateCountdown = () => {
      const now = new Date();
      const diff = RETREAT_DATE - now;

      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setCountdown({ days, hours, minutes, seconds });
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Trigger celebration on first load - lazy load confetti
    if (!loading && booking && !celebrated) {
      setCelebrated(true);
      setTimeout(async () => {
        try {
          const confettiModule = await loadConfetti();
          const confetti = confettiModule.default;
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#2D5016', '#D4AF37', '#C7937A', '#FAF8F5']
          });
        } catch (e) {
          console.log('Confetti animation skipped');
        }
      }, 500);
    }
  }, [loading, booking, celebrated]);

  // Manual VIP toggle for cold visitors: anyone arriving without a booking
  // (no login, no ?booking= param) can self-identify as VIP to preview the
  // VIP-only info. Persisted in sessionStorage so it survives soft navigations.
  const [vipPreview, setVipPreview] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem('retreatWelcome.vipPreview') === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (vipPreview) sessionStorage.setItem('retreatWelcome.vipPreview', '1');
    else sessionStorage.removeItem('retreatWelcome.vipPreview');
  }, [vipPreview]);

  const bookingIsVip = booking?.is_vip || false;
  // Effective VIP state = real VIP booking OR user manually toggled the preview.
  const isVip = bookingIsVip || vipPreview;
  // Only offer the toggle to people whose actual booking isn't already VIP —
  // confirmed VIPs don't need a "show me VIP info" button.
  const showVipToggle = !bookingIsVip;
  const faqs = isVip ? VIP_FAQS : STANDARD_FAQS;

  // Skeleton Loading State with better UX
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sand/30 to-white">
        {/* Hero Skeleton */}
        <div className="bg-forest text-white py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Skeleton className="h-12 w-3/4 mx-auto bg-white/20 mb-4" />
            <Skeleton className="h-6 w-1/2 mx-auto bg-white/20" />
          </div>
        </div>
        
        {/* Countdown Skeleton */}
        <div className="max-w-4xl mx-auto px-4 -mt-8 relative z-20">
          <Card className="shadow-xl border-0">
            <CardContent className="p-6">
              <div className="text-center mb-4">
                <Skeleton className="h-4 w-48 mx-auto mb-2" />
                <Skeleton className="h-5 w-64 mx-auto" />
              </div>
              <div className="grid grid-cols-4 gap-4 max-w-lg mx-auto">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="text-center">
                    <Skeleton className="h-20 w-full rounded-xl mb-2" />
                    <Skeleton className="h-3 w-12 mx-auto" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Content Skeleton */}
        <div className="max-w-4xl mx-auto px-4 py-12 space-y-8">
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-8 w-48 mb-4" />
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-24 w-full rounded-lg" />
                ))}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-8 w-64 mb-4" />
              <Skeleton className="h-32 w-full rounded-lg" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sand/30 to-white">
      {/* Hero Section */}
      <div className="relative bg-forest text-white py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 text-6xl">🌴</div>
          <div className="absolute top-20 right-20 text-4xl">✨</div>
          <div className="absolute bottom-10 left-1/4 text-5xl">🦋</div>
          <div className="absolute bottom-20 right-1/3 text-4xl">🌺</div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          {isVip && (
            <Badge className="bg-amber-500 text-white mb-4 text-sm px-4 py-1">
              <Crown className="h-4 w-4 mr-1 inline" />
              VIP Experience
            </Badge>
          )}
          
          <h1 className="font-heading text-4xl md:text-6xl font-bold mb-4">
            Welcome to Costa Rica! 🎉
          </h1>
          
          <p className="text-xl md:text-2xl text-white/90 mb-2">
            {booking?.full_name ? `${booking.full_name.split(' ')[0]}, your` : 'Your'} spot is officially confirmed
          </p>
          
          {booking?.room_name && (
            <p className="text-lg text-white/80">
              {booking.room_name}
            </p>
          )}
        </div>
      </div>

      {/* Countdown Timer */}
      <div className="max-w-4xl mx-auto px-4 -mt-8 relative z-20">
        <Card className="shadow-xl border-0">
          <CardContent className="p-6">
            <div className="text-center mb-4">
              <p className="text-sm text-gray-500 uppercase tracking-wider">Countdown to Transformation</p>
              <p className="text-forest font-medium">June 26 - July 1, 2026 • Imiloa Institute, Costa Rica</p>
            </div>
            
            <div className="grid grid-cols-4 gap-4 max-w-lg mx-auto">
              {[
                { value: countdown.days, label: 'Days' },
                { value: countdown.hours, label: 'Hours' },
                { value: countdown.minutes, label: 'Minutes' },
                { value: countdown.seconds, label: 'Seconds' }
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="bg-forest text-white rounded-xl py-4 px-2 mb-2">
                    <span className="text-3xl md:text-4xl font-bold">{item.value}</span>
                  </div>
                  <span className="text-xs text-gray-500 uppercase">{item.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-8">
        
        {/* Experience Preview Gallery */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="bg-forest p-4 text-center">
              <h2 className="font-heading text-xl text-white">What Awaits You in Costa Rica</h2>
              <p className="text-white/80 text-sm mt-1">Six days of transformation, sisterhood & self-discovery</p>
            </div>
            <div className="grid md:grid-cols-3 gap-0">
              <div className="relative group overflow-hidden">
                <img 
                  src={RETREAT_IMAGES.yoga} 
                  alt="Stillness and soul work" 
                  className="w-full h-48 md:h-56 object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                  <p className="text-white p-4 font-medium">Stillness + Soul</p>
                </div>
              </div>
              <div className="relative group overflow-hidden">
                <img 
                  src={RETREAT_IMAGES.meditation} 
                  alt="Unmasking and breakthroughs" 
                  className="w-full h-48 md:h-56 object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                  <p className="text-white p-4 font-medium">Unmasking + Breakthroughs</p>
                </div>
              </div>
              <div className="relative group overflow-hidden">
                <img 
                  src={RETREAT_IMAGES.sisterhood} 
                  alt="Sisters connecting and laughing" 
                  className="w-full h-48 md:h-56 object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                  <p className="text-white p-4 font-medium">Sisterhood & Connection</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card className="border-l-4 border-l-terracotta">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 bg-terracotta/10 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-terracotta" />
              </div>
              <h2 className="font-heading text-2xl text-forest">Your Next Steps</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex gap-4 items-start p-4 bg-sand/30 rounded-lg">
                <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">1</div>
                <div>
                  <h3 className="font-semibold text-forest">Secure Your Airfare</h3>
                  <p className="text-gray-600 text-sm mt-1">
                    Book your flight to San Jose (SJO) International Airport. 
                    {isVip 
                      ? " As a VIP, arrive by 2:00 PM on June 25, 2026."
                      : " Arrive by 7:00 AM on June 26, 2026."
                    }
                  </p>
                  <Button variant="outline" size="sm" className="mt-2" asChild>
                    <a href="https://www.google.com/travel/flights" target="_blank" rel="noopener noreferrer">
                      Search Flights <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </Button>
                </div>
              </div>
              
              {isVip && (
                <div className="flex gap-4 items-start p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="h-8 w-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">2</div>
                  <div>
                    <h3 className="font-semibold text-amber-800">Book Your VIP Night Hotel</h3>
                    <p className="text-amber-700 text-sm mt-1">
                      You'll need a hotel near SJO airport for the night of June 25th (at your own cost). 
                      We'll send you our recommended hotel options soon!
                    </p>
                    <Badge variant="outline" className="mt-2 text-amber-600 border-amber-300">
                      <Hotel className="h-3 w-3 mr-1" /> Hotel TBD
                    </Badge>
                  </div>
                </div>
              )}
              
              <div className="flex gap-4 items-start p-4 bg-sand/30 rounded-lg">
                <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">{isVip ? '3' : '2'}</div>
                <div>
                  <h3 className="font-semibold text-forest">Get Trip Insurance</h3>
                  <p className="text-gray-600 text-sm mt-1">
                    All payments are non-refundable. Trip cancellation insurance is required to protect your investment. 
                    Below are some travel insurance resources to explore.
                  </p>
                  <p className="text-gray-400 text-xs mt-1 italic">
                    Note: Sister Diamonds does not endorse or guarantee any of these providers. These are simply resources for your convenience.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button variant="outline" size="sm" asChild>
                      <a href="https://globalrescue.com/imiloa" target="_blank" rel="noopener noreferrer">
                        Global Rescue <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a href="https://www.generalitravelinsurance.com/find-a-plan" target="_blank" rel="noopener noreferrer">
                        Generali Travel <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a href="https://www.travelexinsurance.com/" target="_blank" rel="noopener noreferrer">
                        Travelex <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-4 items-start p-4 bg-sand/30 rounded-lg">
                <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">{isVip ? '4' : '3'}</div>
                <div>
                  <h3 className="font-semibold text-forest">Connect with Your Sisters</h3>
                  <p className="text-gray-600 text-sm mt-1">
                    Join the Costa Rica Retreat community circle in the Sister Diamonds Inner Circle App to meet the other women joining you on this journey!
                  </p>
                  <p className="text-gray-400 text-xs mt-1 italic">
                    Your login details are in your welcome email.
                  </p>
                  <Button variant="outline" size="sm" className="mt-2" asChild>
                    <a href="https://www.sisterdiamonds.com/login" target="_blank" rel="noopener noreferrer">
                      Log In to the App <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </Button>
                </div>
              </div>

              <div
                className="flex gap-4 items-start p-4 bg-sand/30 rounded-lg"
                data-testid="next-step-5"
              >
                <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">{isVip ? '5' : '4'}</div>
                <div>
                  <h3 className="font-semibold text-forest">
                    Complete Your Retreat Forms
                  </h3>
                  <p className="text-gray-600 text-sm mt-1">
                    Two short forms so we can prepare your arrival. Please
                    submit both before <strong>June 1, 2026</strong>.
                  </p>
                  <ul className="text-gray-600 text-sm mt-2 space-y-1.5">
                    <li className="flex items-start gap-2">
                      <span className="text-terracotta mt-1">•</span>
                      <span>
                        <strong>Imiloa Wellness Form</strong> — required by our
                        venue (dietary, wellness, room prep).
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-terracotta mt-1">•</span>
                      <span>
                        <strong>Sister Diamonds Participant Info Form</strong> —
                        flight details, t-shirt size, areas of focus, and the
                        updated Terms of Service.
                      </span>
                    </li>
                  </ul>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button variant="outline" size="sm" asChild>
                      <a
                        href="https://docs.google.com/forms/d/e/1FAIpQLScmBaHTYakUWGtvQ0HPP6OE7Bg4gLyE--sTRtkOZmxrXHQVgQ/viewform?fbzx=-858092369188630044"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="imiloa-form-link"
                      >
                        Imiloa Form <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </Button>
                    <Button size="sm" className="bg-terracotta hover:bg-terracotta/90" asChild>
                      <Link to="/retreat-participant-form" data-testid="participant-form-link">
                        Participant Info Form <ExternalLink className="h-3 w-3 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>

            </div>
          </CardContent>
        </Card>

        {/* BONUS: App Membership Section */}
        <Card className="border-2 border-forest/30 overflow-hidden">
          <CardContent className="p-0">
            {/* Header Banner - Solid color for better readability */}
            <div className="bg-forest p-5 text-white text-center">
              <div className="flex items-center justify-center gap-3 mb-2">
                <span className="text-3xl">🎁</span>
                <h2 className="font-heading text-2xl font-bold">YOUR BONUS GIFT</h2>
                <span className="text-3xl">🎁</span>
              </div>
              <p className="text-lg font-medium">3-Month Sister Diamonds App Membership</p>
              <p className="text-sm text-white/90 mt-1">A $291 Value — Included with your retreat!</p>
            </div>
            
            <div className="p-6 bg-gradient-to-b from-forest/5 to-white">
              <p className="text-gray-700 text-center mb-4">
                As a retreat attendee, you receive <strong>complimentary access</strong> to the Sister Diamonds app 
                for 3 months — your digital sanctuary before, during, and after Costa Rica.
              </p>
              
              {/* Login Reminder */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-amber-800 text-center text-sm">
                  <strong>🔐 Your login credentials</strong> were sent to your email in the retreat welcome message. 
                  Check your inbox (and spam folder) for your temporary password.
                </p>
              </div>
              
              {/* Benefits Grid */}
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                  <Users className="h-5 w-5 text-terracotta mt-0.5" />
                  <div>
                    <p className="font-medium text-forest">Retreat Sister Circle</p>
                    <p className="text-sm text-gray-600">Connect with your retreat sisters now!</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                  <Calendar className="h-5 w-5 text-terracotta mt-0.5" />
                  <div>
                    <p className="font-medium text-forest">Prep Call Schedule</p>
                    <p className="text-sm text-gray-600">All upcoming calls & Zoom links in one place</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                  <Sparkles className="h-5 w-5 text-terracotta mt-0.5" />
                  <div>
                    <p className="font-medium text-forest">The Vault</p>
                    <p className="text-sm text-gray-600">Exclusive teachings from Crystal & Dr. Nicole</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                  <MessageCircle className="h-5 w-5 text-terracotta mt-0.5" />
                  <div>
                    <p className="font-medium text-forest">SHE AI Guide</p>
                    <p className="text-sm text-gray-600">Your personal digital companion</p>
                  </div>
                </div>
              </div>
              
              {/* CTA Button */}
              <div className="flex justify-center mb-6">
                <Button className="bg-forest hover:bg-forest/90 px-8 py-3" asChild>
                  <Link to="/login">
                    <Users className="h-4 w-4 mr-2" /> Connect with Your Sisters
                  </Link>
                </Button>
              </div>
              
              {/* Billing Info */}
              <div className="bg-sand/50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600">
                  Your complimentary 3-month membership begins today. After your trial, you'll be charged 
                  <strong className="text-forest"> $97/month</strong> to the card on file. 
                  <strong className="text-terracotta"> Cancel anytime</strong> — no questions asked.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* VIP Special Section */}
        {isVip && (
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Crown className="h-6 w-6 text-amber-600" />
                <h2 className="font-heading text-xl text-amber-800">Your VIP Experience</h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-3 bg-white/60 rounded-lg">
                  <Calendar className="h-5 w-5 text-amber-600 mt-1" />
                  <div>
                    <p className="font-medium text-amber-900">Early Arrival</p>
                    <p className="text-sm text-amber-700">June 25, 2026 by 2:00 PM</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-white/60 rounded-lg">
                  <Utensils className="h-5 w-5 text-amber-600 mt-1" />
                  <div>
                    <p className="font-medium text-amber-900">VIP Welcome Dinner</p>
                    <p className="text-sm text-amber-700">6:30 PM on June 25th (included)</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-white/60 rounded-lg">
                  <Sparkles className="h-5 w-5 text-amber-600 mt-1" />
                  <div>
                    <p className="font-medium text-amber-900">Priority Access</p>
                    <p className="text-sm text-amber-700">First access to all experiences</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-white/60 rounded-lg">
                  <MessageCircle className="h-5 w-5 text-amber-600 mt-1" />
                  <div>
                    <p className="font-medium text-amber-900">Personal Concierge</p>
                    <p className="text-sm text-amber-700">Dedicated support throughout</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Arrival Info Summary */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Plane className="h-6 w-6 text-forest" />
              <h2 className="font-heading text-xl text-forest">Arrival Information</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">✈️ Fly Into</h3>
                <p className="text-gray-600">San Jose (SJO) International Airport</p>
                {isVip ? (
                  <p className="text-sm text-amber-600 mt-1">
                    <Clock className="h-3 w-3 inline mr-1" />
                    Arrive by 2:00 PM on June 25, 2026
                  </p>
                ) : (
                  <p className="text-sm text-forest mt-1">
                    <Clock className="h-3 w-3 inline mr-1" />
                    Arrive by 7:00 AM on June 26, 2026
                  </p>
                )}
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">🛩️ Private Charter to Imiloa</h3>
                <p className="text-gray-600">Charter flight from SJO to Quepos (25 min)</p>
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-900 font-medium mb-2">
                    <Clock className="h-3 w-3 inline mr-1" />
                    Charter departs 9:00 AM from SJO Domestic Airport
                  </p>
                  <ul className="text-xs text-amber-800 space-y-1 ml-4">
                    <li>• Be at domestic terminal by <strong>8:15 AM</strong> on arrival day</li>
                    <li>• Domestic airport is a 5-minute walk from international arrivals</li>
                    <li>• We recommend arriving 1 day early to avoid missing group transport</li>
                  </ul>
                </div>
                <div
                  className="mt-3 p-3 bg-red-50 border-l-4 border-red-500 rounded-r-lg"
                  data-testid="charter-weight-limit-notice"
                >
                  <p className="text-sm font-bold text-red-800 mb-1 uppercase tracking-wide">
                    ⚠️ Important: 50 lb Total Weight Limit
                  </p>
                  <p className="text-sm text-red-900">
                    The private charter to Imiloa enforces a strict <strong>50 pound total weight limit per passenger</strong> —
                    this includes <strong>both your carry-on and your checked bag combined</strong>. Please
                    weigh your luggage before you leave home. Excess weight may not be accepted on the charter
                    and could be left behind in San José.
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Can't make group transport? Contact the team for alternative arrangements (at guest expense).
                </p>
                <p className="text-sm text-green-600 mt-2">
                  <CheckCircle2 className="h-3 w-3 inline mr-1" />
                  Charter flight included in your retreat package
                </p>
              </div>
            </div>
            
            {/* Departure Info */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Plane className="h-5 w-5 text-terracotta rotate-45" />
                <h3 className="font-semibold text-gray-800">Departure Information</h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-700 mb-1">📅 Departure Date</h4>
                  <p className="text-gray-600">July 1, 2026</p>
                  <p className="text-sm text-terracotta mt-1">
                    <Clock className="h-3 w-3 inline mr-1" />
                    Charter departs Imiloa mid-morning
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-700 mb-1">✈️ Book Return Flight</h4>
                  <p className="text-gray-600">From San Jose (SJO) Airport</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Departure flight from SJO should be after 1:00PM
                  </p>
                </div>
              </div>
            </div>

            {/* Recommended Hotels */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-2">Recommended Hotels Near SJO Airport</h3>
              <p className="text-gray-600 text-sm">
                Flying in early or leaving late? Here are some nearby hotel options for your convenience.
              </p>
              <p className="text-gray-400 text-xs mt-1 italic">
                Note: Sister Diamonds does not endorse or guarantee any of these hotels. These are simply resources for your convenience.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button variant="outline" size="sm" asChild>
                  <a href="https://www.guestreservations.com/doubletree-by-hilton-cariari-san-jose-costa-rica/booking" target="_blank" rel="noopener noreferrer">
                    DoubleTree by Hilton <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href="https://www.barcelo.com/en-us/barcelo-san-jose/" target="_blank" rel="noopener noreferrer">
                    Barcelo San Jose <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href="https://www.hotelpibiboreal.com/" target="_blank" rel="noopener noreferrer">
                    Hotel Pibi Boreal <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href="https://www.marriott.com/en-us/hotels/sjofa-fairfield-san-jose-airport-alajuela/overview/" target="_blank" rel="noopener noreferrer">
                    Fairfield by Marriott <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* VIP preview toggle — only shown when the visitor isn't already a confirmed VIP */}
        {showVipToggle && (
          <div
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-amber-200/70 bg-amber-50/40 px-5 py-4"
            data-testid="vip-toggle-row"
          >
            <div className="flex items-start sm:items-center gap-3">
              <Crown className="h-5 w-5 text-amber-600 mt-0.5 sm:mt-0 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-forest">
                  Joining as a VIP?
                </p>
                <p className="text-xs text-gray-600">
                  Toggle on to see the VIP schedule, inclusions and $150 spa
                  credit details below.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setVipPreview((v) => !v)}
              data-testid="vip-preview-toggle"
              aria-pressed={vipPreview}
              className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                vipPreview
                  ? 'bg-amber-600 text-white hover:bg-amber-700'
                  : 'bg-white text-amber-700 border border-amber-300 hover:bg-amber-50'
              }`}
            >
              <Crown className="h-4 w-4" />
              {vipPreview ? 'Showing VIP details' : 'Show VIP details'}
            </button>
          </div>
        )}

        {/* VIP-Only: What's included with your VIP add-on */}
        {isVip && (
          <Card
            className="border-2 border-amber-200/70 bg-gradient-to-br from-amber-50/60 to-white"
            data-testid="vip-included-section"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Crown className="h-6 w-6 text-amber-600" />
                <h2 className="font-heading text-xl text-forest">Your VIP Experience</h2>
                <Badge className="bg-amber-600 hover:bg-amber-600 text-white">VIP</Badge>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-heading text-base text-forest mb-3">VIP Schedule</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <Plane className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Arrival:</strong> June 25, 2026 by 2:00 PM</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Hotel className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>Hotel:</strong> Book a room near SJO airport (at your
                        expense — we'll send recommendations)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Utensils className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>VIP Dinner:</strong> 6:30 PM on June 25th (included!)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Calendar className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>Next Day:</strong> Join the group for the private
                        charter flight on June 26th
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-base text-forest mb-3">VIP Includes</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>Private dinner with Crystal &amp; Dr. Nicole in Costa Rica</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>60-min ONE-ON-TWO Executive Coaching Session</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>Wellness bar access (daily smoothies &amp; elixirs)<sup className="text-amber-700">*</sup></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>$150 Spa Credit<sup className="text-amber-700">**</sup></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>Self-Care &amp; Meditation Toolkit</span>
                    </li>
                  </ul>
                  <p className="text-xs text-gray-500 mt-3">
                    <sup>*</sup>Max 3 per day &nbsp;·&nbsp; <sup>**</sup>Gratuity not included
                  </p>
                </div>
              </div>

              <div className="mt-5 p-4 rounded-lg bg-amber-100/40 border border-amber-200/60">
                <p className="text-sm text-gray-700">
                  <strong>Spa packages:</strong>{' '}
                  <a
                    href="https://drive.google.com/file/d/1eAJjTvgq8QzdB8ZZmY365iSMnn9NWRL8/view?usp=sharing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-terracotta underline font-medium inline-flex items-center gap-1"
                    data-testid="vip-spa-packages-link"
                  >
                    Browse spa packages <ExternalLink className="h-3 w-3" />
                  </a>
                  . Bookings are made on-site. Your $150 VIP spa credit will be
                  applied to whichever package you choose.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Universal: What is NOT included (shown to both VIP and non-VIP) */}
        <Card data-testid="exclusions-section" className="border border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="h-6 w-6 text-terracotta" />
              <h2 className="font-heading text-xl text-forest">Your retreat excludes</h2>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              So you can plan accordingly, here's what is <em>not</em> covered by your retreat:
            </p>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-terracotta mt-1">•</span>
                International airfare to/from San José (SJO)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-terracotta mt-1">•</span>
                Travel insurance <strong className="text-terracotta">(Required)</strong>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-terracotta mt-1">•</span>
                Alcoholic beverages (Imiloa is an alcohol-free property)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-terracotta mt-1">•</span>
                Souvenirs &amp; personal shopping
              </li>
              <li className="flex items-start gap-2">
                <span className="text-terracotta mt-1">•</span>
                Laundry services (available at extra cost)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-terracotta mt-1">•</span>
                Spa treatments &amp; specialty drinks {isVip ? '(except VIP credit)' : '(except VIP)'}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-terracotta mt-1">•</span>
                Gratuities for Imiloa staff, spa services, etc.
              </li>
            </ul>

            <div className="mt-5 p-4 rounded-lg bg-sand/40 border border-sand">
              <p className="text-sm text-gray-700">
                <strong>Planning a spa treatment?</strong>{' '}
                <a
                  href="https://drive.google.com/file/d/1eAJjTvgq8QzdB8ZZmY365iSMnn9NWRL8/view?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-terracotta underline font-medium inline-flex items-center gap-1"
                  data-testid="universal-spa-packages-link"
                >
                  Browse spa packages <ExternalLink className="h-3 w-3" />
                </a>
                . Bookings are made on-site at Imiloa.
                {isVip && ' Your $150 VIP spa credit will be applied to your chosen package.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* FAQs */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Sun className="h-6 w-6 text-forest" />
              <h2 className="font-heading text-xl text-forest">
                {isVip ? 'VIP ' : ''}Frequently Asked Questions
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`faq-${index}`}>
                  <AccordionTrigger className="text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center py-8">
          <p className="text-gray-600 mb-4">Questions? Contact us at <a href="mailto:info@volitionenterprises.com" className="text-terracotta hover:underline">info@volitionenterprises.com</a> - we're here to help.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild className="bg-forest hover:bg-forest/90">
              <Link to="/circles/vip-costa-rica">
                <Users className="h-4 w-4 mr-2" />
                Join Retreat Circle
              </Link>
            </Button>
            {user && (
              <Button variant="outline" asChild>
                <Link to="/dashboard">
                  Go to Dashboard <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetreatWelcomePage;
